import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ABGroupsShowcase from "@/components/ABGroupsShowcase";
import {
  FlaskConical, Users, GraduationCap, ArrowRight, BookOpen,
  CheckCircle2, FileDown, ShieldCheck, ScanLine,
  Layers, Type,
} from "lucide-react";

/* ---------------- Inline decorative SVGs ----------------
   Kept inline (no extra HTTP requests) and themed to the brutalist palette.
   Tailwind classes on the wrapper control size/position. */

function AtomSVG({ className = "" }) {
  return (
    <svg viewBox="0 0 100 100" className={className} fill="none"
         stroke="#0F172A" strokeWidth="3" aria-hidden="true">
      <circle cx="50" cy="50" r="6" fill="#0F172A" />
      <ellipse cx="50" cy="50" rx="42" ry="16" transform="rotate(0 50 50)" />
      <ellipse cx="50" cy="50" rx="42" ry="16" transform="rotate(60 50 50)" />
      <ellipse cx="50" cy="50" rx="42" ry="16" transform="rotate(120 50 50)" />
      <circle cx="92" cy="50" r="3.5" fill="#FEF08A" stroke="#0F172A" />
      <circle cx="29" cy="86" r="3.5" fill="#D1FAE5" stroke="#0F172A" />
      <circle cx="29" cy="14" r="3.5" fill="#E0F2FE" stroke="#0F172A" />
    </svg>
  );
}

function BeakerSVG({ className = "" }) {
  return (
    <svg viewBox="0 0 100 110" className={className} fill="none"
         stroke="#0F172A" strokeWidth="3.5" strokeLinejoin="round" aria-hidden="true">
      <path d="M35 12 L35 42 L15 80 Q10 95 26 95 L74 95 Q90 95 85 80 L65 42 L65 12 Z"
            fill="#D1FAE5" />
      <path d="M30 12 L70 12" strokeLinecap="round" />
      <path d="M22 70 L78 70" />
      <circle cx="40" cy="82" r="3" fill="#0F172A" />
      <circle cx="56" cy="76" r="4" fill="#0F172A" />
      <circle cx="48" cy="55" r="2.5" fill="#0F172A" />
      <circle cx="62" cy="85" r="2" fill="#0F172A" />
    </svg>
  );
}

function PeriodicGridSVG({ className = "" }) {
  const cells = [
    { x: 0, y: 0, fill: "#FFEDD5", label: "H", num: "1" },
    { x: 130, y: 0, fill: "#D1FAE5", label: "He", num: "2" },
    { x: 0, y: 22, fill: "#FEF08A", label: "Li", num: "3" },
    { x: 22, y: 22, fill: "#E0F2FE", label: "Be", num: "4" },
    { x: 66, y: 22, fill: "#FFEDD5", label: "B", num: "5" },
    { x: 88, y: 22, fill: "#FFEDD5", label: "C", num: "6" },
    { x: 110, y: 22, fill: "#E0F2FE", label: "N", num: "7" },
    { x: 132, y: 22, fill: "#E0F2FE", label: "O", num: "8" },
    { x: 154, y: 22, fill: "#D1FAE5", label: "F", num: "9" },
    { x: 0, y: 44, fill: "#FEF08A", label: "Na", num: "11" },
    { x: 22, y: 44, fill: "#E0F2FE", label: "Mg", num: "12" },
    { x: 132, y: 44, fill: "#E0F2FE", label: "S", num: "16" },
    { x: 154, y: 44, fill: "#D1FAE5", label: "Cl", num: "17" },
  ];
  return (
    <svg viewBox="0 0 175 65" className={className} aria-hidden="true">
      {cells.map((c) => (
        <g key={c.num}>
          <rect x={c.x} y={c.y} width="20" height="20" rx="1"
                fill={c.fill} stroke="#0F172A" strokeWidth="1.4" />
          <text x={c.x + 2.5} y={c.y + 5.5} fontSize="3"
                fill="#0F172A" fontWeight="700">{c.num}</text>
          <text x={c.x + 10} y={c.y + 15} fontSize="6.5"
                fill="#0F172A" fontWeight="900" textAnchor="middle">{c.label}</text>
        </g>
      ))}
    </svg>
  );
}

function MoleculeBondSVG({ className = "" }) {
  return (
    <svg viewBox="0 0 120 60" className={className} fill="none"
         stroke="#0F172A" strokeWidth="2.5" aria-hidden="true">
      <line x1="20" y1="30" x2="50" y2="30" />
      <line x1="70" y1="30" x2="100" y2="30" />
      <circle cx="15" cy="30" r="9" fill="#FEF08A" />
      <circle cx="60" cy="30" r="10" fill="#FFFFFF" />
      <circle cx="105" cy="30" r="9" fill="#D1FAE5" />
      <text x="15" y="34" fontSize="9" fill="#0F172A" fontWeight="900"
            textAnchor="middle" stroke="none">H</text>
      <text x="60" y="34" fontSize="9" fill="#0F172A" fontWeight="900"
            textAnchor="middle" stroke="none">O</text>
      <text x="105" y="34" fontSize="9" fill="#0F172A" fontWeight="900"
            textAnchor="middle" stroke="none">H</text>
    </svg>
  );
}

/* ---------------- Reusable bits ---------------- */

const BRUT = "border-2 border-[#0F172A] shadow-[4px_4px_0px_#0F172A]";
const BRUT_HOVER =
  "transition-all duration-200 ease-in-out hover:-translate-y-[3px] " +
  "hover:shadow-[6px_6px_0px_#0F172A] active:translate-y-[1px] " +
  "active:shadow-[2px_2px_0px_#0F172A]";

function SectionLabel({ children }) {
  return (
    <div className="inline-block">
      <span className="text-[11px] tracking-[0.25em] uppercase font-black text-[#0F172A]
                       bg-[#FEF08A] px-3 py-1 border-2 border-[#0F172A]">
        {children}
      </span>
    </div>
  );
}

/* ---------------- Page ---------------- */

export default function Landing() {
  const { t } = useTranslation();
  const [poolRounded, setPoolRounded] = useState(null);

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${base}/api/public/pool-count`)
      .then((r) => r.json())
      .then((d) => setPoolRounded(d.rounded))
      .catch(() => {/* silent — fallback copy will just omit the count */});
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A]">
      {/* Beta tester banner — full-width, above sticky header */}
      <div
        className="bg-[#FEF08A] border-b-2 border-[#0F172A] text-[#0F172A]"
        data-testid="beta-banner"
      >
        <div className="max-w-6xl mx-auto px-6 py-2.5 text-center text-xs sm:text-sm font-bold">
          <span className="mr-1">🧪</span>
          <span className="uppercase tracking-wider mr-2">Faza beta:</span>
          testuj bez obaw — konto usuniesz sam w 2 kliknięciach z
          <Link to="/teacher/settings#danger"
                className="underline underline-offset-2 mx-1"
                data-testid="beta-banner-settings-link">
            Ustawień → Strefa niebezpieczna
          </Link>
          (możesz też najpierw pobrać ZIP ze swoimi danymi).
        </div>
      </div>

      {/* Sticky header */}
      <header className="sticky top-0 z-50 bg-[#F8FAFC]/85 backdrop-blur-md
                         border-b-2 border-[#0F172A]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-logo">
            <div className={`${BRUT} bg-[#FEF08A] w-11 h-11 flex items-center
                              justify-center rounded-md`}>
              <FlaskConical className="w-5 h-5" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display font-black text-lg leading-none tracking-tight">
                {t("landing.brand.name")}
              </div>
              <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#64748B]">
                {t("landing.brand.tagline")}
              </div>
            </div>
          </Link>
          <LanguageSwitcher />
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        {/* Faint decorative grid in background */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.04]"
             style={{ backgroundImage:
               "linear-gradient(#0F172A 1px, transparent 1px), linear-gradient(90deg, #0F172A 1px, transparent 1px)",
               backgroundSize: "32px 32px" }} />
        {/* Floating atom on the right (desktop only) */}
        <AtomSVG className="hidden lg:block absolute top-12 right-[-40px] w-72 h-72
                            opacity-90 animate-[spin_22s_linear_infinite]" />
        <MoleculeBondSVG className="hidden md:block absolute bottom-6 left-[-10px]
                                    w-44 h-24 opacity-70" />

        <div className="relative max-w-6xl mx-auto px-6 pt-16 pb-24">
          <div className="max-w-3xl">
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-7xl
                           tracking-tight leading-[0.95]">
              {t("landing.hero.title_line1")}<br />
              <span className="inline-block mt-3">{t("landing.hero.title_line2")}</span>
            </h1>
            <p className="mt-7 text-base sm:text-lg lg:text-xl text-[#334155]
                          max-w-2xl leading-relaxed">
              {t("landing.hero.subtitle_lead")}
              <b className="text-[#0F172A]"> {t("landing.hero.subtitle_highlight")}</b>
            </p>

            {/* CTA cards */}
            <div className="mt-12 grid sm:grid-cols-2 gap-5 max-w-2xl">
              <Link to="/teacher/login" data-testid="cta-teacher-link" className="group">
                <div className={`${BRUT} ${BRUT_HOVER} bg-[#FFEDD5] rounded-md p-6 h-full`}>
                  <div className="flex items-center justify-between">
                    <div className="bg-white border-2 border-[#0F172A] w-12 h-12
                                    flex items-center justify-center rounded-md">
                      <GraduationCap className="w-6 h-6" strokeWidth={2.5} />
                    </div>
                    <span className="text-[10px] tracking-[0.2em] uppercase font-black
                                     bg-white border-2 border-[#0F172A] px-2 py-0.5">
                      {t("landing.cta.teacher_badge")}
                    </span>
                  </div>
                  <div className="font-display font-black text-2xl mt-4 leading-tight">
                    {t("landing.cta.teacher_title")}
                  </div>
                  <div className="text-sm text-[#334155] mt-1">
                    {t("landing.cta.teacher_desc")}
                  </div>
                  <div className="mt-5 flex items-center gap-2 font-bold text-sm
                                  group-hover:translate-x-1 transition-transform">
                    {t("landing.cta.enter")} <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                  </div>
                </div>
              </Link>

              <Link to="/join" data-testid="cta-student-link" className="group">
                <div className={`${BRUT} ${BRUT_HOVER} bg-[#E0F2FE] rounded-md p-6 h-full`}>
                  <div className="flex items-center justify-between">
                    <div className="bg-white border-2 border-[#0F172A] w-12 h-12
                                    flex items-center justify-center rounded-md">
                      <Users className="w-6 h-6" strokeWidth={2.5} />
                    </div>
                    <span className="text-[10px] tracking-[0.2em] uppercase font-black
                                     bg-white border-2 border-[#0F172A] px-2 py-0.5">
                      {t("landing.cta.student_badge")}
                    </span>
                  </div>
                  <div className="font-display font-black text-2xl mt-4 leading-tight">
                    {t("landing.cta.student_title")}
                  </div>
                  <div className="text-sm text-[#334155] mt-1">
                    {t("landing.cta.student_desc")}
                  </div>
                  <div className="mt-5 flex items-center gap-2 font-bold text-sm
                                  group-hover:translate-x-1 transition-transform">
                    {t("landing.cta.enter")} <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                  </div>
                </div>
              </Link>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
              <Link to="/instrukcja"
                    className="font-bold underline underline-offset-4 inline-flex
                               items-center gap-1.5" data-testid="instrukcja-link">
                <BookOpen className="w-4 h-4" strokeWidth={2.5} />{t("landing.cta.instrukcja")}
              </Link>
              <span className="text-[#94A3B8]">·</span>
              <Link to="/register" className="font-bold underline underline-offset-4"
                    data-testid="register-link-landing">
                {t("landing.cta.register_link")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Jak to działa — 2 kolumny */}
      <section className="bg-white border-y-2 border-[#0F172A]">
        <div className="max-w-6xl mx-auto px-6 py-20 lg:py-24">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <SectionLabel>Jak to działa</SectionLabel>
              <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl
                             mt-4 leading-tight">
                Od pomysłu do wyników<br />w&nbsp;3 krokach.
              </h2>
            </div>
            <BeakerSVG className="w-24 h-28 hidden sm:block" />
          </div>

          {/* Single grid: mobile stacks (Nauczyciel block, then Uczeń block) using
              source order; lg uses CSS order to align pairs side-by-side so each
              row's max height stretches both teacher card and student card. */}
          <div className="grid lg:grid-cols-2 gap-x-8 gap-y-4 items-stretch">
            {/* Chip: Dla Nauczyciela */}
            <div className="lg:order-1 mb-1">
              <span className="bg-[#FEF08A] border-2 border-[#0F172A] px-3 py-1
                               text-xs font-black uppercase tracking-[0.15em]">
                Dla Nauczyciela
              </span>
            </div>
            {(() => {
              const teacherSteps = [
                { n: "1", title: "Wybierz pytania",
                  desc: poolRounded
                    ? `Ponad ${poolRounded} pytań w 7 zakresach — każde sprawdzone przez AI. Możesz też dodać własne pytania.`
                    : "Duża pula pytań w 7 zakresach — każde sprawdzone przez AI. Możesz też dodać własne pytania.",
                  icon: Layers, order: "lg:order-3" },
                { n: "2", title: "Udostępnij kod",
                  desc: "Uczniowie skanują QR lub wpisują 6-znakowy kod sesji.",
                  icon: ScanLine, order: "lg:order-5" },
                { n: "3", title: "Pobierz wyniki",
                  desc: "PDF i CSV — indywidualnie i zbiorczo. Statystyki per pytanie.",
                  icon: FileDown, order: "lg:order-7" },
              ];
              return teacherSteps.map((s) => (
                <StepCard key={s.n} step={s} bg="bg-[#FEF08A]"
                          testid={`feature-step-${s.n}`}
                          orderClass={s.order} />
              ));
            })()}

            {/* Chip: Dla Ucznia */}
            <div className="lg:order-2 mb-1 mt-6 lg:mt-0">
              <span className="bg-[#E0F2FE] border-2 border-[#0F172A] px-3 py-1
                               text-xs font-black uppercase tracking-[0.15em]">
                Dla Ucznia
              </span>
            </div>
            {(() => {
              const studentSteps = [
                { n: "A", title: "Dołącz do kartkówki",
                  desc: "Z dowolnego urządzenia — telefon, tablet, laptop. Bez instalacji, bez konta.",
                  icon: Users, order: "lg:order-4" },
                { n: "B", title: "Wpisz kod + imię i nazwisko",
                  desc: "6 znaków od nauczyciela. Imię i nazwisko — bez maila, bez innych danych osobowych.",
                  icon: Type, order: "lg:order-6" },
                { n: "C", title: "Rozwiązuj — odpowiedzi się zapisują",
                  desc: "Auto-save co 1.2 s. Odśwież stronę, zmień urządzenie — postęp pozostaje.",
                  icon: CheckCircle2, order: "lg:order-8" },
              ];
              return studentSteps.map((s) => (
                <StepCard key={s.n} step={s} bg="bg-[#E0F2FE]"
                          orderClass={s.order} />
              ));
            })()}
          </div>
        </div>
      </section>

      {/* 90s animated showcase — Grupy A/B anti-cheat feature */}
      <ABGroupsShowcase />

      {/* Co potrafi ocena — features bento */}
      <section className="max-w-6xl mx-auto px-6 py-20 lg:py-24 relative">
        <PeriodicGridSVG className="absolute top-8 right-6 w-60 h-24 hidden lg:block opacity-90" />
        <div className="max-w-3xl">
          <SectionLabel>Inteligentne sprawdzanie</SectionLabel>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl mt-4
                         leading-tight">
            Aplikacja rozumie,<br />że uczniowie piszą szybko.
          </h2>
          <p className="text-base sm:text-lg text-[#334155] mt-4 max-w-2xl">
            Algorytm oceniania uwzględnia drobne literówki, brak polskich diakrytyk,
            zapis indeksowy wzorów i jednostki w odpowiedzi.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {[
            { title: "Polskie znaki opcjonalne",
              ex: '„wegla" = „węgla"',
              icon: Type, color: "bg-[#D1FAE5]" },
            { title: "Wzory chemiczne",
              ex: '„H2O" = „H₂O" = „H_2O"',
              icon: FlaskConical, color: "bg-[#E0F2FE]" },
            { title: "Tolerancja literówek",
              ex: '„siakra" → „siarka" (1 zamiana)',
              icon: CheckCircle2, color: "bg-[#FEF08A]" },
            { title: "Jednostki opcjonalne",
              ex: '„48" = „48 g" = „48g"',
              icon: ScanLine, color: "bg-[#FFEDD5]" },
            { title: "2FA + alert nowego logowania",
              ex: "Bezpieczeństwo nauczyciela",
              icon: ShieldCheck, color: "bg-[#D1FAE5]" },
          ].map((f, i) => (
            <div key={i} className={`${BRUT} ${BRUT_HOVER} bg-white rounded-md p-5`}>
              <div className={`${BRUT} ${f.color} w-12 h-12 flex items-center
                                justify-center rounded-md mb-3`}>
                <f.icon className="w-5 h-5" strokeWidth={2.5} />
              </div>
              <div className="font-display font-black text-lg">{f.title}</div>
              <div className="text-sm text-[#334155] mt-1 font-mono">{f.ex}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA stripe */}
      <section className="bg-[#0F172A] text-white border-t-2 border-[#0F172A]">
        <div className="max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-3 gap-10 items-center">
          <div className="lg:col-span-2">
            <div className="text-[11px] tracking-[0.25em] uppercase font-black text-[#FEF08A]">
              Zacznij teraz
            </div>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl
                           mt-3 leading-tight">
              Pierwsza kartkówka w&nbsp;<span className="bg-[#FEF08A] text-[#0F172A] px-2">5&nbsp;minut</span>.
            </h2>
            <p className="text-sm sm:text-base text-[#CBD5E1] mt-3 max-w-xl">
              Konto darmowe. Bez karty. Bez limitów na ilość uczniów.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <Link to="/register" data-testid="cta-register-bottom">
              <Button className={`${BRUT} ${BRUT_HOVER} bg-[#FEF08A] text-[#0F172A]
                                    font-black text-base rounded-md w-full h-12
                                    hover:bg-white`}>
                Załóż konto nauczyciela
              </Button>
            </Link>
            <Link to="/instrukcja" data-testid="cta-instrukcja-bottom">
              <Button className={`${BRUT} ${BRUT_HOVER} bg-white text-[#0F172A]
                                    font-bold text-base rounded-md w-full h-12`}>
                Zobacz instrukcję
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t-2 border-[#0F172A]">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-wrap gap-6
                        items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`${BRUT} bg-[#FEF08A] w-9 h-9 flex items-center
                              justify-center rounded-md`}>
              <FlaskConical className="w-4 h-4" strokeWidth={2.5} />
            </div>
            <div className="text-sm">
              <div className="font-black">Kartkówki Chemia</div>
              <div className="text-[#64748B] text-xs">© {new Date().getFullYear()} · LO klasy 1–4</div>
            </div>
          </div>
          <nav className="flex flex-wrap gap-4 text-sm font-bold">
            <Link to="/instrukcja" className="underline underline-offset-4">Instrukcja</Link>
            <Link to="/terms" className="underline underline-offset-4">Regulamin</Link>
            <Link to="/privacy" className="underline underline-offset-4">Polityka prywatności</Link>
            <Link to="/status" className="underline underline-offset-4" data-testid="footer-status-link">Status</Link>
            <Link to="/teacher/login" className="underline underline-offset-4">Logowanie</Link>
            <Link to="/teacher/settings#danger"
                  className="underline underline-offset-4 text-[#B91C1C]"
                  data-testid="footer-delete-account-link"
                  title="Wymaga zalogowania — link prowadzi do Ustawienia → Strefa niebezpieczna">
              Usuń konto
            </Link>
            <a href={`mailto:${t("common.contact_email")}`}
               className="underline underline-offset-4"
               data-testid="footer-contact-link">
              {t("common.contact_email")}
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
}

/* StepCard — shared by both teacher (1/2/3) and student (A/B/C) columns.
   `orderClass` is a Tailwind `lg:order-N` so CSS Grid can interleave items
   into matching rows on desktop (auto-equalizing heights via Grid stretch),
   while on mobile the source order naturally groups teacher cards then
   student cards. `h-full` on the inner card guarantees the visible BRUT
   shadow box fills the stretched grid cell. */
function StepCard({ step, bg, orderClass, testid }) {
  const Icon = step.icon;
  const BRUT = "border-2 border-[#0F172A] shadow-[4px_4px_0px_#0F172A]";
  return (
    <div className={`${orderClass}`} data-testid={testid}>
      <div className={`${BRUT} bg-white rounded-md p-5 flex gap-4 items-start h-full`}>
        <div className={`${BRUT} ${bg} w-14 h-14 flex items-center
                          justify-center rounded-md shrink-0`}>
          <Icon className="w-6 h-6" strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.25em] uppercase font-black
                          text-[#64748B]">Krok {step.n}</div>
          <div className="font-display font-black text-xl mt-0.5">{step.title}</div>
          <div className="text-sm text-[#334155] mt-1">{step.desc}</div>
        </div>
      </div>
    </div>
  );
}

